import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { BadgeCheck, Trophy, Database, Filter, Swords, Terminal, ArrowRight, ShieldCheck, Github, Globe2, Star, MessageCircle, FileCheck2 } from "lucide-react";

const SKILL_BADGES = [
  { label: "Data Modeling Master", icon: Database, color: "#5C9DFF" },
  { label: "SQL Sharpshooter", icon: Terminal, color: "#34D399" },
  { label: "Segmentation Expert", icon: Filter, color: "#F59E0B" },
  { label: "Boss Battle Champion", icon: Swords, color: "#B58BFF" },
];

export const ForRecruitersShowcase = () => {
  return (
    <section className="max-w-7xl mx-auto px-6 py-16" data-testid="for-recruiters-showcase">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
        {/* Left: candidate profile mockup */}
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ duration: 0.6 }}
          className="relative rounded-3xl border border-white/10 bg-[#0A101D]/80 backdrop-blur-xl p-6 sm:p-7 order-2 lg:order-1"
          data-testid="candidate-profile-mockup"
        >
          <span
            className="absolute -top-3 right-6 text-[9px] font-bold uppercase tracking-wider text-slate-400 bg-[#0A101D] border border-white/15 rounded-full px-3 py-1 select-none"
            data-testid="candidate-mockup-sample-tag"
            title="This is an illustrative example — not a real candidate."
          >
            Sample Profile — Illustrative
          </span>
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#5C9DFF]/40 to-[#B58BFF]/30 border border-white/20 flex items-center justify-center font-heading font-bold text-xl text-white">PS</div>
            <div className="flex-1">
              <div className="flex items-center gap-1.5">
                <p className="font-heading font-semibold text-white">Priya Sharma</p>
                <BadgeCheck className="w-4 h-4 text-[#5C9DFF]" />
              </div>
              <p className="text-xs text-slate-400">Data Cloud Consultant · Verified Performer</p>
              <span className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider text-emerald-300/80 mt-1.5 cursor-default select-none" data-testid="candidate-mockup-verified-by-ai">
                <ShieldCheck className="w-2.5 h-2.5" /> Verified by AI
              </span>
            </div>
            <div className="text-right shrink-0">
              <p className="text-[10px] text-slate-500 uppercase tracking-wider">Global Rank</p>
              <p className="font-heading text-xl font-bold text-amber-400" data-testid="candidate-mockup-global-rank">#47</p>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3 mt-6">
            <div className="rounded-xl border border-white/10 bg-white/[0.03] p-3 text-center">
              <Trophy className="w-4 h-4 text-[#5C9DFF] mx-auto mb-1.5" strokeWidth={1.5} />
              <p className="text-sm font-bold text-white">3,140</p>
              <p className="text-[10px] text-slate-500">Total XP</p>
            </div>
            <div className="rounded-xl border border-white/10 bg-white/[0.03] p-3 text-center">
              <Swords className="w-4 h-4 text-violet-300 mx-auto mb-1.5" strokeWidth={1.5} />
              <p className="text-sm font-bold text-white">5 / 5</p>
              <p className="text-[10px] text-slate-500">Bosses Cleared</p>
            </div>
            <div className="rounded-xl border border-white/10 bg-white/[0.03] p-3 text-center">
              <ShieldCheck className="w-4 h-4 text-emerald-400 mx-auto mb-1.5" strokeWidth={1.5} />
              <p className="text-sm font-bold text-white">91%</p>
              <p className="text-[10px] text-slate-500">Interview Score</p>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3 mt-3">
            <div className="rounded-xl border border-white/10 bg-white/[0.03] p-3 text-center">
              <FileCheck2 className="w-4 h-4 text-cyan-300 mx-auto mb-1.5" strokeWidth={1.5} />
              <p className="text-sm font-bold text-white">96%</p>
              <p className="text-[10px] text-slate-500">ATS Score</p>
            </div>
            <div className="rounded-xl border border-white/10 bg-white/[0.03] p-3 text-center">
              <MessageCircle className="w-4 h-4 text-amber-300 mx-auto mb-1.5" strokeWidth={1.5} />
              <p className="text-sm font-bold text-white">88%</p>
              <p className="text-[10px] text-slate-500">Communication</p>
            </div>
            <div className="rounded-xl border border-white/10 bg-white/[0.03] p-3 text-center">
              <Star className="w-4 h-4 text-yellow-300 mx-auto mb-1.5" strokeWidth={1.5} />
              <p className="text-sm font-bold text-white">4.9 / 5</p>
              <p className="text-[10px] text-slate-500">Community Rating</p>
            </div>
          </div>

          <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mt-6 mb-2.5">Skill Badges — Earned, Not Claimed</p>
          <div className="flex flex-wrap gap-2" data-testid="candidate-mockup-skill-badges">
            {SKILL_BADGES.map((b) => {
              const Icon = b.icon;
              return (
                <span
                  key={b.label} data-testid={`skill-badge-pill-${b.label.toLowerCase().replace(/\s+/g, "-")}`}
                  title="Earned automatically by clearing real checkpoints — not clickable here."
                  className="flex items-center gap-1.5 text-[11px] font-semibold px-3 py-1.5 rounded-full border cursor-default select-none"
                  style={{ borderColor: `${b.color}40`, color: b.color, background: `${b.color}12` }}
                >
                  <Icon className="w-3 h-3" /> {b.label}
                </span>
              );
            })}
          </div>

          <div className="flex items-center gap-3 mt-5 pt-5 border-t border-white/5" data-testid="candidate-mockup-links">
            <span title="Illustrative — real candidates connect their own GitHub here." className="flex items-center gap-1.5 text-[11px] font-semibold text-slate-300/80 cursor-default select-none"><Github className="w-3 h-3" /> GitHub</span>
            <span title="Illustrative — real candidates connect their own portfolio here." className="flex items-center gap-1.5 text-[11px] font-semibold text-slate-300/80 cursor-default select-none"><Globe2 className="w-3 h-3" /> Portfolio</span>
            <span className="text-[10px] text-slate-500">— instead of a resume</span>
          </div>
        </motion.div>

        {/* Right: text */}
        <div className="order-1 lg:order-2">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-400">For Recruiters</span>
          <h2 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight mt-3 leading-tight">
            These aren't resumes. <br /> They're verified performers.
          </h2>
          <p className="text-slate-400 mt-4 leading-relaxed max-w-lg">
            Every candidate in Cloudivo's Talent Directory has a real, AI-verified track record — Boss Battles cleared, live-scored mock interviews, and skill badges earned by doing the work, not writing about it. Filter by verified skill, not keyword-stuffed PDFs.
          </p>
          <ul className="mt-6 space-y-3">
            {["Skill badges tied to real, completed checkpoints — not self-reported", "Live AI interview scores across Technical, SQL & System Design", "Global rank computed from actual XP, not a resume template"].map((line) => (
              <li key={line} className="flex items-start gap-2.5 text-sm text-slate-300">
                <BadgeCheck className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" /> {line}
              </li>
            ))}
          </ul>
          <Link
            to="/recruiter" data-testid="for-recruiters-cta"
            className="inline-flex items-center gap-2 mt-8 text-sm font-semibold text-white bg-emerald-500 hover:bg-emerald-400 px-6 py-3 rounded-xl transition-all hover:-translate-y-0.5"
          >
            Hire Verified Performers <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
};
